import { Token } from '@/components/molecules'
import { gasLanes } from '@/constants'
import { toWeiFromGwei } from '@/utils'
import { InfoIcon } from '@chakra-ui/icons'
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableCaption,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react'
import Big from 'big.js'
import { formatEther } from 'viem'

export const CalculateSubRequest = ({
  register,
  callback,
  handleSubmit,
  setValue,
  result,
  maxResult,
  calcInitData: {
    averageVerificationGas,
    maxCallbackGasLimit,
    gasPrice,
    protocolFee,
  },
  errors,
  ...rest
}) => {
  const onCallbackFocus = (e) => {
    if (!Number(e.target.value)) {
      e.target.select()
    }
  }

  const calculate = ({ callbackGas, gasPrice, gasLane }) => {
    const finalCallbackGas = Math.min(callbackGas, maxCallbackGasLimit)
    const gasSpent = Big(finalCallbackGas).plus(averageVerificationGas)

    if (finalCallbackGas === maxCallbackGasLimit) {
      setValue('callbackGas', maxCallbackGasLimit)
    }

    const gasPriceInWei = toWeiFromGwei(Number(gasPrice))
    const gasLaneInWei = toWeiFromGwei(Number(gasLane))

    const gasCostInWei = Big(gasPriceInWei).times(gasSpent).toFixed(0)
    const maxGasCostInWei = Big(gasLaneInWei).times(gasSpent)

    // Format gas prices to ether
    const gasCostInEther = formatEther(gasCostInWei.toString())
    const maxGasCostInEther = formatEther(maxGasCostInWei.toString())

    // Calculate results
    const result = Big(gasCostInEther).plus(protocolFee).toString()
    const maxResult = Big(maxGasCostInEther).plus(protocolFee).toString()

    return { result, maxResult }
  }

  const onSubmit = ({ callbackGas, gasPrice, gasLane }) => {
    const result = calculate({ callbackGas, gasPrice, gasLane })

    callback(result)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} {...rest}>
      <TableContainer
        overflow={'hidden'}
        whiteSpace={'none'}
        fontSize={['sm', 'md']}
      >
        <Table variant="simple">
          <TableCaption>Subscription Calculation</TableCaption>
          <Thead>
            <Tr>
              <Th>Input</Th>
              <Th>Value</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>
                Gas price (current is {gasPrice} gwei){' '}
                <Tooltip label="The current gas price, which fluctuates depending on network conditions.">
                  <InfoIcon />
                </Tooltip>
              </Td>
              <Td>
                <FormControl isInvalid={errors.gasPrice}>
                  <Input
                    fontSize={['sm', 'md']}
                    id="gasPrice"
                    {...register('gasPrice', {
                      pattern: {
                        value: /^(0|[1-9]\d*)(\.\d+)?$/,
                        message: 'Should be a number',
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {errors.gasPrice && errors.gasPrice.message}
                  </FormErrorMessage>
                </FormControl>
              </Td>
            </Tr>
            <Tr>
              <Td>
                Callback gas (max. {maxCallbackGasLimit})
                <Tooltip label="The amount of gas used for the callback request that returns your requested random values.">
                  <InfoIcon />
                </Tooltip>
              </Td>
              <Td>
                <Input
                  fontSize={['sm', 'md']}
                  onFocus={onCallbackFocus}
                  {...register('callbackGas', { valueAsNumber: true })}
                  type="number"
                />
              </Td>
            </Tr>
            <Tr>
              <Td>
                Average verification gas{' '}
                <Tooltip label="The amount of gas used to verify randomness onchain.">
                  <InfoIcon />
                </Tooltip>
              </Td>
              <Td>{averageVerificationGas}</Td>
            </Tr>
            <Tr>
              <Td>
                Gas lane (Hash){' '}
                <Tooltip label="The maximum gas price you are willing to pay for a request in wei.">
                  <InfoIcon />
                </Tooltip>
              </Td>
              <Td>
                <RadioGroup
                  defaultValue={gasLanes[0].value}
                  colorScheme={'purple'}
                >
                  <Stack direction="column">
                    {gasLanes.map(({ value, label }, idx) => (
                      <Radio key={idx} {...register('gasLane')} value={value}>
                        {label}
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
              </Td>
            </Tr>
            <Tr>
              <Td>Protocol Fee</Td>
              <Td>{protocolFee} FTN</Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
      <VStack mt={4} spacing={2}>
        <Button variant={'solid'} type="submit">
          Calculate
        </Button>
        <VStack>
          <Flex gap={2}>
            <Text color={'primary.400'} fontWeight={500} as={'span'}>
              Estimated cost per request:
            </Text>
            <Token
              amount={result || 0}
              toFtn={false}
              symbol={'FTN'}
              decimal={8}
            />
          </Flex>
          <Flex gap={2}>
            <Text color={'primary.400'} fontWeight={500} as={'span'}>
              Maximum cost per request under the selected gas lane:
            </Text>
            <Token
              amount={maxResult || 0}
              toFtn={false}
              symbol={'FTN'}
              decimal={8}
            />
          </Flex>
        </VStack>
      </VStack>
    </form>
  )
}
